import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ShoppingCart, User, LogOut, MessageCircle, ChevronDown, Globe } from 'lucide-react';
import { useCart } from '../context/CartContext.jsx';
import { useAuth } from '../context/AuthContext.jsx';
import { useLanguage } from '../context/LanguageContext.jsx';
import AuthModal from './AuthModal';
import { HUB_PATH, HUB_SECTIONS } from '../data/researchHub';

const FOCUS_RING = 'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#00E5FF]';

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [researchOpen, setResearchOpen] = useState(false);
  const [mobileResearchOpen, setMobileResearchOpen] = useState(false);
  const researchRef = useRef(null);
  const location = useLocation();
  const { toggleCart, getCartTotal, loadCartFromServer, isSaving, lastSavedAt } = useCart();
  // ── Cart-sync indicator state ──────────────────────────────────────
  // Shows a brief "Synced ✓" pulse after each successful server-save so
  // logged-in users can see their cart changes were persisted. Auto-hides
  // 2.5s after the last save.
  const [showSyncedPulse, setShowSyncedPulse] = React.useState(false);
  React.useEffect(() => {
    if (!lastSavedAt) return;
    setShowSyncedPulse(true);
    const t = setTimeout(() => setShowSyncedPulse(false), 2500);
    return () => clearTimeout(t);
  }, [lastSavedAt]);
  const { user, isAuthenticated, logout } = useAuth();
  const { itemCount } = getCartTotal();
  const { t } = useLanguage();

  const navigation = [
    { name: t('nav.home'), path: '/' },
    { name: t('nav.products'), path: '/products' },
    { name: t('nav.labSupplies'), path: '/lab-supplies' },
    { name: t('nav.about'), path: '/about' },
    { name: t('nav.blog'), path: '/blog' },
    { name: t('nav.contact'), path: '/contact' }
  ];

  const researchDropdownItems = [
    { name: t('researchDropdown.guide'), path: HUB_PATH, testId: 'research-guide' },
    ...HUB_SECTIONS.map((s) => ({ name: s.navLabel, path: `${HUB_PATH}#${s.id}`, testId: s.id })),
  ];
  const RESEARCH_PATHS = [HUB_PATH, '/glp1-research', '/tirzepatide-research'];

  const isActive = (path) => location.pathname === path;
  const isResearchActive = RESEARCH_PATHS.includes(location.pathname);
  const researchToggleRef = useRef(null);

  // Close dropdown on outside click/tap
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (researchRef.current && !researchRef.current.contains(e.target)) {
        setResearchOpen(false);
      }
    };
    document.addEventListener('pointerdown', handleClickOutside);
    return () => document.removeEventListener('pointerdown', handleClickOutside);
  }, []);

  // Escape closes the research menu and returns focus to its toggle
  useEffect(() => {
    if (!researchOpen) return undefined;
    const handleEsc = (e) => {
      if (e.key !== 'Escape') return;
      setResearchOpen(false);
      researchToggleRef.current?.focus();
    };
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [researchOpen]);

  useEffect(() => { setResearchOpen(false); }, [location.pathname, location.hash]);

  const handleLogin = () => {
    setShowAuthModal(true);
  };

  const handleLogout = async () => {
    await logout();
  };

  // Load cart from server when user logs in
  React.useEffect(() => {
    if (isAuthenticated) {
      loadCartFromServer();
    }
  }, [isAuthenticated, loadCartFromServer]);

  return (
    <>
      <header className="navigation-header">
        <div className="container">
          <div className="flex items-center justify-between">
            <div style={{ position: 'relative', display: 'inline-block' }}>
              <Link to="/" className="navigation-logo">
                <div
                  data-testid="header-logo-container"
                  className="rcm-logo-container"
                  style={{
                    /* Use the global `.rcm-logo-container` styling
                     * (white pill, padding, radius). Only keep a small
                     * padding override + radius tweak to match the
                     * header's compact look — do NOT re-declare the
                     * background here, the class owns that. */
                    padding: '8px 14px',
                    borderRadius: '10px',
                  }}
                >
                  <img
                    src="/rcm-logo-2026.webp"
                    alt="RCM Biosciences — Research Peptide Company"
                    width="150" height="111" fetchpriority="high" decoding="async"
                    className="header-logo-img"
                    style={{ maxHeight: '54px' }}
                  />
                </div>
              </Link>
              <Link to="/admin" style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', width: '100%', height: '100%', zIndex: 2 }} data-testid="header-manage-link">
                <span style={{ display: 'none' }}>Manage</span>
              </Link>
            </div>

            {/* Desktop Navigation */}
            <nav className="hidden lg:block">
              <ul className="navigation-menu">
                {navigation.slice(0, 3).map((item) => (
                  <li key={item.name}>
                    <Link
                      to={item.path}
                      className={`navigation-link ${isActive(item.path) ? 'active' : ''}`}
                    >
                      {item.name}
                    </Link>
                  </li>
                ))}

                {/* Research: the link goes to the hub; the chevron button is the
                    only way to open/close the submenu (mouse click, tap, Enter/Space).
                    Hover never toggles it, so a click can't fight a hover-open. */}
                <li
                  ref={researchRef}
                  style={{ position: 'relative', display: 'inline-flex', alignItems: 'center', gap: '2px' }}
                  onBlur={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) setResearchOpen(false); }}
                  data-testid="research-dropdown"
                >
                  <Link
                    to={HUB_PATH}
                    className={`navigation-link ${isResearchActive ? 'active' : ''} ${FOCUS_RING}`}
                    onClick={() => setResearchOpen(false)}
                    data-testid="research-dropdown-trigger"
                  >
                    {t('nav.research')}
                  </Link>
                  <button
                    type="button"
                    ref={researchToggleRef}
                    onClick={() => setResearchOpen((o) => !o)}
                    aria-expanded={researchOpen}
                    aria-controls="research-submenu"
                    aria-label={researchOpen ? 'Close Research menu' : 'Open Research menu'}
                    className={`navigation-link rounded ${FOCUS_RING}`}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '6px', display: 'inline-flex', alignItems: 'center', color: 'inherit' }}
                    data-testid="research-dropdown-toggle"
                  >
                    <ChevronDown
                      size={14}
                      aria-hidden="true"
                      style={{
                        transition: 'transform 0.2s ease',
                        transform: researchOpen ? 'rotate(180deg)' : 'rotate(0deg)'
                      }}
                    />
                  </button>

                  {researchOpen && (
                    <div
                      id="research-submenu"
                      style={{
                        position: 'absolute',
                        top: '100%',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        minWidth: '240px',
                        background: 'rgba(5, 11, 26, 0.98)',
                        backdropFilter: 'blur(16px)',
                        border: '1px solid var(--border-light)',
                        boxShadow: '0 8px 32px rgba(0,0,0,0.4), 0 0 12px rgba(0, 229, 255, 0.06)',
                        zIndex: 100,
                        paddingTop: '8px',
                        paddingBottom: '8px',
                        marginTop: '4px',
                        borderRadius: '8px'
                      }}
                      data-testid="research-dropdown-menu"
                    >
                      {/* Visual spacer between the trigger and the panel. */}
                      <span
                        aria-hidden="true"
                        style={{
                          position: 'absolute',
                          top: '-8px',
                          left: 0,
                          right: 0,
                          height: '8px',
                        }}
                      />
                      <ul aria-label="Research areas">
                        {researchDropdownItems.map((item, i) => (
                          <li key={item.path} style={i === 0 ? { borderBottom: '1px solid var(--border-light)', marginBottom: '4px', paddingBottom: '4px' } : undefined}>
                            <Link
                              to={item.path}
                              onClick={() => setResearchOpen(false)}
                              className={`block hover:bg-[rgba(0,229,255,0.1)] hover:text-[#00E5FF] ${FOCUS_RING}`}
                              style={{
                                padding: '10px 20px',
                                fontSize: '14px',
                                fontWeight: i === 0 ? '600' : '400',
                                color: i === 0 ? 'var(--brand-primary)' : 'var(--text-secondary)',
                                textDecoration: 'none',
                                transition: 'background 0.15s, color 0.15s',
                                whiteSpace: 'nowrap'
                              }}
                              data-testid={`research-link-${item.testId}`}
                            >
                              {item.name}
                            </Link>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </li>

                {navigation.slice(3).map((item) => (
                  <li key={item.name}>
                    <Link
                      to={item.path}
                      className={`navigation-link ${isActive(item.path) ? 'active' : ''}`}
                    >
                      {item.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </nav>

            <div className="flex items-center gap-3">
              {/* User Login/Account */}
              {isAuthenticated ? (
                <div className="hidden md:flex items-center gap-3">
                  <Link
                    to="/order-history"
                    className="body-small"
                    style={{ 
                      color: 'var(--brand-primary)', 
                      textDecoration: 'none',
                      fontWeight: '500'
                    }}
                  >
                    {t('nav.orderHistory')}
                  </Link>
                  <Link
                    to="/wishlist"
                    className="body-small"
                    style={{ 
                      color: 'var(--brand-primary)', 
                      textDecoration: 'none',
                      fontWeight: '500'
                    }}
                  >
                    {t('nav.wishlist')}
                  </Link>
                  <span data-clarity-mask="True" style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                    Hi, {user?.name?.split(' ')[0]}
                  </span>
                  <button
                    onClick={handleLogout}
                    style={{
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      padding: '6px',
                      color: 'var(--text-secondary)'
                    }}
                    title="Sign out"
                  >
                    <LogOut size={18} />
                  </button>
                </div>
              ) : (
                <button
                  onClick={handleLogin}
                  className="hidden md:flex items-center gap-2"
                  style={{
                    background: 'none',
                    border: '1px solid var(--border-medium)',
                    borderRadius: '8px',
                    padding: '8px 12px',
                    cursor: 'pointer',
                    fontSize: '0.875rem',
                    color: 'var(--text-secondary)',
                    transition: 'all 0.2s ease'
                  }}
                >
                  <User size={16} />
                  {t('nav.signIn')}
                </button>
              )}

              {/* Community Button — disabled, set REACT_APP_COMMUNITY_ENABLED=true to re-enable */}
              {process.env.REACT_APP_COMMUNITY_ENABLED === 'true' && (
                <Link
                  to="/community"
                  data-testid="header-community-btn"
                  className="community-btn"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    background: isActive('/community') ? 'var(--brand-primary)' : 'var(--rcm-electric)',
                    color: '#000',
                    padding: '8px 16px',
                    borderRadius: '6px',
                    textDecoration: 'none',
                    fontSize: '0.875rem',
                    fontWeight: '600',
                    whiteSpace: 'nowrap'
                  }}
                >
                  <MessageCircle size={16} />
                  <span className="hidden sm:inline">{t('nav.community')}</span>
                </Link>
              )}

              {/* Language Toggle — REMOVED 2026-07-18. Site is English-only.
                  Kept the surrounding markup in-place so downstream layout
                  (nav grid, cart alignment) stays identical. */}

              {/* Cart Icon - Always visible */}
              <button
                onClick={toggleCart}
                className="relative"
                style={{ 
                  background: 'none', 
                  border: 'none', 
                  cursor: 'pointer',
                  padding: '8px'
                }}
                data-testid="cart-icon-btn"
                aria-label="Open shopping cart"
              >
                <ShoppingCart size={24} style={{ color: 'var(--brand-primary)' }} />
                {itemCount > 0 && (
                  <span
                    style={{
                      position: 'absolute',
                      top: '0',
                      right: '0',
                      background: '#dc2626',
                      color: 'white',
                      borderRadius: '50%',
                      width: '18px',
                      height: '18px',
                      fontSize: '11px',
                      fontWeight: 'bold',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                    data-testid="cart-item-count"
                  >
                    {itemCount}
                  </span>
                )}
                {/* Sync status indicator — bottom-right of the cart icon */}
                {(isSaving || showSyncedPulse) && (
                  <span
                    data-testid={isSaving ? 'cart-sync-saving' : 'cart-sync-saved'}
                    title={isSaving ? 'Syncing cart to your account…' : 'Cart synced'}
                    style={{
                      position: 'absolute',
                      bottom: '2px',
                      right: '2px',
                      background: isSaving ? '#f59e0b' : '#10b981',
                      color: 'white',
                      borderRadius: '50%',
                      width: '10px',
                      height: '10px',
                      fontSize: '0',
                      border: '2px solid var(--bg-primary)',
                      transition: 'background-color 0.3s ease',
                      animation: isSaving ? 'pulse 1.4s ease-in-out infinite' : 'none',
                    }}
                  />
                )}
              </button>

              {/* Mobile/Tablet Menu Button */}
              <button
                className="lg:hidden mobile-menu-toggle"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                aria-label="Toggle menu"
              >
                {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
              </button>
            </div>
          </div>

          {/* Mobile/Tablet Navigation */}
          {mobileMenuOpen && (
            <nav className="lg:hidden mt-4 pb-4">
              <ul className="flex flex-col space-y-2">
                {navigation.slice(0, 3).map((item) => (
                  <li key={item.name}>
                    <Link
                      to={item.path}
                      className={`block py-2 navigation-link ${isActive(item.path) ? 'active' : ''}`}
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      {item.name}
                    </Link>
                  </li>
                ))}

                {/* Mobile Research Dropdown */}
                <li>
                  <button
                    type="button"
                    onClick={() => setMobileResearchOpen(!mobileResearchOpen)}
                    onKeyDown={(e) => { if (e.key === 'Escape') setMobileResearchOpen(false); }}
                    aria-expanded={mobileResearchOpen}
                    aria-controls="mobile-research-submenu"
                    className={`block py-2 navigation-link w-full text-left ${isResearchActive ? 'active' : ''} ${FOCUS_RING}`}
                    style={{
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      fontFamily: 'inherit',
                      fontSize: 'inherit',
                      fontWeight: 'inherit',
                      color: 'inherit'
                    }}
                    data-testid="mobile-research-dropdown-trigger"
                  >
                    {t('nav.research')}
                    <ChevronDown
                      size={16}
                      style={{
                        transition: 'transform 0.2s ease',
                        transform: mobileResearchOpen ? 'rotate(180deg)' : 'rotate(0deg)'
                      }}
                    />
                  </button>
                  {mobileResearchOpen && (
                    <ul
                      id="mobile-research-submenu"
                      className="pl-4 space-y-1 mt-1"
                      aria-label="Research areas"
                      onKeyDown={(e) => { if (e.key === 'Escape') setMobileResearchOpen(false); }}
                      data-testid="mobile-research-submenu"
                    >
                      {researchDropdownItems.map((item) => (
                        <li key={item.path}>
                          <Link
                            to={item.path}
                            className={`block py-2 navigation-link ${FOCUS_RING}`}
                            onClick={() => { setMobileMenuOpen(false); setMobileResearchOpen(false); }}
                            style={{ fontSize: '14px' }}
                            data-testid={`mobile-research-link-${item.testId}`}
                          >
                            {item.name}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  )}
                </li>

                {navigation.slice(3).map((item) => (
                  <li key={item.name}>
                    <Link
                      to={item.path}
                      className={`block py-2 navigation-link ${isActive(item.path) ? 'active' : ''}`}
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      {item.name}
                    </Link>
                  </li>
                ))}
                <li className="pt-2 border-t" style={{ borderColor: 'var(--border-light)' }}>
                  {isAuthenticated ? (
                    <div className="flex items-center justify-between py-2">
                      <span data-clarity-mask="True" style={{ color: 'var(--text-secondary)' }}>
                        {user?.name}
                      </span>
                      <button
                        onClick={() => { handleLogout(); setMobileMenuOpen(false); }}
                        style={{
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          color: 'var(--brand-primary)',
                          fontWeight: '500'
                        }}
                      >
                        {t('nav.signOut')}
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => { setShowAuthModal(true); setMobileMenuOpen(false); }}
                      className="w-full py-2 text-left navigation-link"
                    >
                      {t('nav.signIn')}
                    </button>
                  )}
                </li>
              </ul>
            </nav>
          )}
        </div>
      </header>

      <AuthModal 
        isOpen={showAuthModal} 
        onClose={() => setShowAuthModal(false)} 
      />
    </>
  );
};

export default Header;
